import { logger } from '@/utils/logger';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useAuth } from '@/hooks/useAuth';
import {
  checkEmailDuplicate,
  sendVerificationCode,
  signup,
  verifyConfirmCode,
} from '@/services/auth/emailAuthService';
import { useAuthStore } from '@/stores/authStore';
import { AgreementState } from '@/types/agreement_types';
import { validatePassword } from '@/utils/authValidation';
import { sanitizeInput } from '@/utils/xssProtection';
import { useState } from 'react';

interface UseSignInSignUpModalReturn {
  // Form states
  formData: {
    email: string;
    password: string;
    passwordConfirm: string;
    name: string;
    verificationCode: string;
  };
  errors: Record<string, string>;
  emailSuccessMessage: string;
  isLoading: boolean;

  // Agreement states
  isAgreementValid: boolean;
  agreements: AgreementState | null;

  // Email verification states
  isEmailSent: boolean;
  isVerified: boolean;
  isSendingCode: boolean;
  isVerifyingCode: boolean;

  // Form actions
  updateFormData: (field: string, value: string) => void; // 로그인용
  updateFormDataWithValidation: (field: string, value: string) => void; // 회원가입용 추가
  handleSignIn: () => Promise<any>;
  handleSignUp: () => Promise<any>;
  setAgreementState: (valid: boolean, state: AgreementState) => void;
  resetForm: () => void;
  handleSendVerificationCode: (isResend?: boolean) => Promise<boolean>;
  handleVerifyCode: () => Promise<void>;
  isFormValid: () => boolean;
  validateField: (field: string, value: string) => string;
}

export const useSignInSignUpModal = (): UseSignInSignUpModalReturn => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    passwordConfirm: '',
    name: '',
    verificationCode: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [emailSuccessMessage, setEmailSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAgreementValid, setIsAgreementValid] = useState(false);
  const [agreements, setAgreements] = useState<AgreementState | null>(null);

  // 이메일 인증 관련 상태
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);

  const { login } = useAuth();
  const { setUser } = useAuthStore();

  const updateFormData = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // 이메일이 변경되면 인증 상태 초기화
    if (field === 'email') {
      setIsEmailSent(false);
      setIsVerified(false);
      setFormData(prev => ({ ...prev, verificationCode: '' }));
      setEmailSuccessMessage('');
    }

    // 기존 에러만 클리어
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    // // 실시간 유효성 검사 (값이 있을 때만)
    // if (value || field === 'passwordConfirm') {
    //   // 패스워드 확인은 항상 검사
    //   const error = validateField(field, value);
    //   setErrors(prev => ({ ...prev, [field]: error }));
    // } else {
    //   // 값이 비어있으면 에러 제거
    //   setErrors(prev => ({ ...prev, [field]: '' }));
    // }
  };

  // 전체 폼 유효성 검사 함수
  const isFormValid = (): boolean => {
    // 모든 필드가 채워졌는지 확인
    const isAllFieldsFilled =
      formData.email && formData.password && formData.passwordConfirm && formData.name && formData.verificationCode;

    // 에러가 없는지 확인
    const hasNoErrors = Object.values(errors).every(error => !error);

    // 이메일 인증 완료 확인
    const isEmailVerified = isVerified;

    // 약관 동의 확인
    const isAgreementsValid = isAgreementValid;

    return !!(isAllFieldsFilled && hasNoErrors && isEmailVerified && isAgreementsValid);
  };

  const setAgreementState = (valid: boolean, state: AgreementState) => {
    setIsAgreementValid(valid);
    setAgreements(state);
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      passwordConfirm: '',
      name: '',
      verificationCode: '',
    });
    setErrors({});
    setIsAgreementValid(false);
    setAgreements(null);
    setIsEmailSent(false);
    setIsVerified(false);
    setEmailSuccessMessage('');
  };
  // 인증 코드 발송 함수
  const handleSendVerificationCode = async (isResend = false): Promise<boolean> => {
    setIsSendingCode(true);
    setErrors({});

    try {
      // 이메일 중복 체크 (sanitize input before sending)
      const sanitizedEmail = sanitizeInput(formData.email);
      const emailCheckResult = await checkEmailDuplicate(sanitizedEmail);
      const isAvailable = emailCheckResult.data?.available;

      if (isAvailable === false) {
        setErrors({ email: 'This email is already registered. Please sign in.' });
        setEmailSuccessMessage('');
        return false;
      }

      // 인증 코드 발송 (sanitize input before sending)
      const response = await sendVerificationCode(sanitizedEmail);

      if (response.success) {
        setIsEmailSent(true);
        setEmailSuccessMessage(
          isResend
            ? 'A new verification code has been sent to your email.'
            : 'Verification code sent! Please check your email.',
        );
        return true;
      } else {
        setErrors({ email: response.message || 'Verification code sending failed.' });
        setEmailSuccessMessage('');
        return false;
      }
    } catch (error) {
      logger.error('Error in handleSendVerificationCode:', { error: error });

      if (!errors.email) {
        const errorMessage = error instanceof Error ? error.message : 'An error occurred while checking email.';
        setErrors({ email: errorMessage });
      }
      setEmailSuccessMessage('');
      return false;
    } finally {
      setIsSendingCode(false);
    }
  };

  // 인증 코드 확인 함수 - 서버 API를 통해 검증
  const handleVerifyCode = async () => {
    setIsVerifyingCode(true);
    setErrors({});

    try {
      if (!formData.verificationCode) {
        setErrors({ verificationCode: 'Please enter a verification code.' });
        return;
      }

      // 서버 API를 통해 인증 코드 검증
      const response = await verifyConfirmCode(
        sanitizeInput(formData.email),
        sanitizeInput(formData.verificationCode)
      );

      if (response.success) {
        setIsVerified(true);
        setErrors({});
      } else {
        setIsVerified(false);
        setErrors({ verificationCode: response.message || 'The verification code is incorrect. Please try again.' });
      }
    } catch (error) {
      setIsVerified(false);
      setErrors({ verificationCode: 'An error occurred while verifying the code.' });
    } finally {
      setIsVerifyingCode(false);
    }
  };
  // 로그인 처리 함수
  const handleSignIn = async () => {
    setIsLoading(true);
    setErrors({});

    try {
      // 1. 기본 빈 값 검사
      if (!formData.email.trim() || !formData.password) {
        const newErrors: Record<string, string> = {};
        if (!formData.email.trim()) newErrors.email = 'Invalid Email';
        if (!formData.password) newErrors.password = 'Password Mismatch';
        setErrors(newErrors);
        return { success: false };
      }

      // 2. 이메일 중복 체크로 이메일 존재 여부 확인 (sanitize input before sending)
      try {
        const emailCheckResult = await checkEmailDuplicate(sanitizeInput(formData.email));
        const isAvailable = emailCheckResult.data?.available;

        if (isAvailable === true) {
          setErrors({
            email: 'Invalid Email',
          });
          return { success: false };
        }
      } catch (emailCheckError) {
        logger.error('Email check error:', emailCheckError);
      }

      // 3. 로그인 시도 (sanitize input before sending)
      const response = await login(sanitizeInput(formData.email), sanitizeInput(formData.password));

      if (response && response.success === true) {
        setUser(response.user || null);
        return response;
      }

      // 로그인 실패
      setErrors({
        password: 'Password Mismatch',
      });
      return { success: false };
    } catch (error) {
      logger.error('Sign in error:', { error: error });
      setErrors({
        password: 'Password Mismatch',
      });
      return { success: false, error };
    } finally {
      setIsLoading(false);
    }
  };
  // 회원가입 처리 함수
  const handleSignUp = async () => {
    setIsLoading(true);
    setErrors({});

    try {
      // 전체 유효성 검사
      const allErrors: Record<string, string> = {};

      Object.keys(formData).forEach(field => {
        const error = validateField(field, formData[field as keyof typeof formData]);
        if (error) {
          allErrors[field] = error;
        }
      });

      if (Object.keys(allErrors).length > 0) {
        setErrors(allErrors);
        return { success: false };
      }

      // 이메일 인증 확인
      if (!isVerified) {
        setErrors({ verificationCode: 'Please complete email verification.' });
        return { success: false };
      }

      // 약관 동의 확인
      if (!isAgreementValid) {
        return { success: false };
      }

      // 회원가입 API 호출 (sanitize input before sending)
      const response = await signup({
        email: sanitizeInput(formData.email),
        password1: sanitizeInput(formData.password),
        password2: sanitizeInput(formData.passwordConfirm),
        confirm_code: sanitizeInput(formData.verificationCode),
        full_name: sanitizeInput(formData.name),
      });

      if (response.success) {
        return {
          success: true,
          message: response.message || 'Sign up completed successfully!',
        };
      } else {
        // 인증 코드 관련 에러인 경우 verificationCode 필드에 에러 표시
        const codeErrors = ['EXPIRED_CODE', 'INVALID_CODE', 'EMAIL_NOT_FOUND'];
        if (response.errorCode && codeErrors.includes(response.errorCode)) {
          setIsVerified(false);
          setErrors({ verificationCode: response.message || 'Sign up failed.' });
        } else {
          setErrors({ email: response.message || 'Sign up failed.' });
        }
        return { success: false };
      }
    } catch (error) {
      logger.error('Sign up error:', { error: error });
      setErrors({ email: 'An error occurred on the server.' });
      return { success: false, error };
    } finally {
      setIsLoading(false);
    }
  };

  // 유효성 검사 함수들
  const validateField = (field: string, value: string): string => {
    switch (field) {
      case 'email': {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value) return 'Please enter an email.';
        if (!emailRegex.test(value)) return 'Invalid email format.';
        return '';
      }

      case 'password': {
        if (!value) return 'Please enter a password.';
        const passwordResult = validatePassword(value);
        return passwordResult;
      }

      case 'passwordConfirm': {
        if (!value) return 'Please confirm your password.';
        if (value !== formData.password) return 'Passwords do not match.';
        return '';
      }

      case 'name': {
        if (!value) return 'Please enter your name.';
        if (value.length < 2) return 'Name must be at least 2 characters.';
        if (value.length > 20) return 'Name must be less than 20 characters.';
        return '';
      }

      case 'verificationCode': {
        if (!value) return 'Please enter verification code.';
        if (value.length !== 6 || !/^\d+$/.test(value)) return 'Verification code must be 6 digits.';
        return '';
      }

      default:
        return '';
    }
  };

  // 회원가입용 - 실시간 검증 있음
  const updateFormDataWithValidation = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // 이메일이 변경되면 인증 상태 초기화
    if (field === 'email') {
      setIsEmailSent(false);
      setIsVerified(false);
      setFormData(prev => ({ ...prev, verificationCode: '' }));
      setEmailSuccessMessage('');
    }

    // 실시간 유효성 검사
    if (value || field === 'passwordConfirm') {
      const error = validateField(field, value);
      setErrors(prev => ({ ...prev, [field]: error }));
    } else {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return {
    // States
    formData,
    errors,
    emailSuccessMessage,
    isLoading,
    isAgreementValid,
    agreements,
    isEmailSent,
    isVerified,
    isSendingCode,
    isVerifyingCode,

    // Actions
    updateFormData,
    handleSignIn,
    handleSignUp,
    setAgreementState,
    resetForm,
    handleSendVerificationCode,
    handleVerifyCode,
    isFormValid,
    validateField,
    updateFormDataWithValidation,
  };
};
